import { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import Select from "react-select";
import moment from "moment";
import { toast } from "react-toastify";

import {
  updateTicket,
  closeTicket,
  addTicketWorkLog,
  getAllTicketClosureTypes,
  getAllTicketLoggedTypes,
} from "../Api/TicketApiOrg";

// const TicketEditModal = ({
//   show,
//   onClose,
//   ticket,
//   orgId,
//   userDetails,
//   projectOptions,
//   ticketAssignees,
//   refreshTickets,
// }) => {
const TicketEditModal = ({
  show,
  onClose,
  ticket,
  orgId,
  userDetails,
  projectOptions,
  ticketAssignees,
  assigneeOptionsByProject,
  fetchProjectAndPartners,
  refreshTickets,
}) => {
  const [loading, setLoading] = useState(false);

  const [showClosureModal, setShowClosureModal] = useState(false);
  const [showWorkLogModal, setShowWorkLogModal] = useState(false);

  const [ticketReasons, setTicketReasons] = useState([]);
  const [closureTypes, setClosureTypes] = useState([]);

  const [updateForm, setUpdateForm] = useState({
    ticketName: "",
    description: "",
    status: "",
    priority: "",
    assignedTo: [],
    submittedBy: "",
    category: "",
    dueDate: "",
    referenceTo: "",
    projectAssigned: "",
    ticketType: "",
    ticketReason: "",
  });

  const [dynamicAssignees, setDynamicAssignees] = useState([]);

  useEffect(() => {
    if (!updateForm.projectAssigned) {
      setDynamicAssignees([]);
      return;
    }

    const projectIds = updateForm.projectAssigned.split(",");

    const map = new Map();

    projectIds.forEach((pid) => {
      const assignees = assigneeOptionsByProject?.[pid] || [];

      assignees.forEach((opt) => {
        map.set(opt.value, opt);
      });
    });

    setDynamicAssignees(Array.from(map.values()));
  }, [updateForm.projectAssigned, assigneeOptionsByProject]);

  const [workLogForm, setWorkLogForm] = useState({
    workedBy: "",
    effortValue: "",
    effortUnit: "HOURS",
    remarks: "",
  });

  const [closureForm, setClosureForm] = useState({
    closureTypeCode: "",
    closureRemarks: "",
    efforts: [
      {
        workedBy: "",
        effortValue: "",
        effortUnit: "HOURS",
      },
    ],
  });

  useEffect(() => {
    if (!ticket) return;

    setUpdateForm({
      ticketName: ticket.ticketName || "",
      description: ticket.description || "",
      status: ticket.status || "Open",
      priority: ticket.priority || "Medium",
      assignedTo: ticket.assignedTo
        ? ticket.assignedTo.split(",").map((i) => i.trim())
        : [],
      submittedBy: ticket.submittedBy || "",
      category: ticket.category || "",
      referenceTo: ticket.referenceTo || "",
      dueDate: ticket.dueDate
        ? moment(ticket.dueDate).format("YYYY-MM-DD")
        : "",
      projectAssigned: ticket.projectAssigned || "",
      ticketType: ticket.ticketType || "Internal",
      ticketReason: ticket.ticketReason || "",
      editedBy: String(userDetails.userId),
    });
  }, [ticket]);

  useEffect(() => {
    loadTicketReasons();
    loadClosureTypes();
  }, []);

  const loadTicketReasons = async () => {
    try {
      const res = await getAllTicketLoggedTypes();
      setTicketReasons(res || []);
    } catch {
      toast.error("Failed to load ticket reasons");
    }
  };

  const loadClosureTypes = async () => {
    try {
      const res = await getAllTicketClosureTypes();
      setClosureTypes(res?.data || []);
    } catch {
      toast.error("Failed to load closure types");
    }
  };

  const handleUpdateTicket = async () => {
    try {
      setLoading(true);

      const payload = {
        ticketId: ticket.ticketId,
        ...updateForm,
        assignedTo: updateForm.assignedTo.join(","),
        editedBy: String(userDetails.userId),
      };

      await updateTicket(ticket.ticketId, payload);

      toast.success("Ticket updated successfully");

      refreshTickets();
      onClose();
    } catch (error) {
      toast.error("Failed to update ticket");
    } finally {
      setLoading(false);
    }
  };

  const handleAddWorkLog = async () => {
    try {
      if (!workLogForm.workedBy || !workLogForm.effortValue) {
        toast.error("Fill all fields");
        return;
      }

      const payload = {
        ticketId: ticket.ticketId,
        orgId,
        createdBy: userDetails.userId,
        changedBy: userDetails.userId,
        workedBy: workLogForm.workedBy,
        effortValue: Number(workLogForm.effortValue),
        effortUnit: workLogForm.effortUnit,
        closureRemarks: workLogForm.remarks,
      };

      await addTicketWorkLog(payload);

      toast.success("Work log added");
      setShowWorkLogModal(false);
    } catch {
      toast.error("Failed to add work log");
    }
  };

  const handleConfirmClosure = async () => {
    try {
      const payload = {
        ticketId: ticket.ticketId,
        orgId,
        closureTypeCode: closureForm.closureTypeCode,
        closureRemarks: closureForm.closureRemarks,
        createdBy: userDetails.userId,
        efforts: closureForm.efforts,
      };

      await closeTicket(payload);

      toast.success("Ticket closed successfully");

      setShowClosureModal(false);
      onClose();
      refreshTickets();
    } catch {
      toast.error("Failed to close ticket");
    }
  };

  const ticketReasonOptions = ticketReasons.map((t) => ({
    value: t.code,
    label: t.descriptions,
  }));

  const closureTypeOptions = closureTypes.map((c) => ({
    value: c.codefix,
    label: c.description,
  }));

  if (!ticket) return null;

  return (
    <>
      <Modal show={show} onHide={onClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Ticket {ticket.ticketId}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="fields-form">
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Ticket Name <span className="text-red">**</span>
                  </Form.Label>

                  <Form.Control
                    type="text"
                    value={updateForm.ticketName}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        ticketName: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>

                  <Form.Select
                    value={updateForm.status}
                    onChange={(e) => {
                      const value = e.target.value;

                      setUpdateForm({
                        ...updateForm,
                        status: value,
                      });

                      if (value === "Closed") {
                        setShowClosureModal(true);
                      }
                    }}
                  >
                    <option value="Open">Open</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>

                  <Form.Select
                    value={updateForm.priority}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        priority: e.target.value,
                      })
                    }
                  >
                    <option value="Low">Low</option>
                    <option value="Normal">Normal</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ticket Type</Form.Label>

                  <Form.Select
                    value={updateForm.ticketType}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        ticketType: e.target.value,
                      })
                    }
                  >
                    <option value="Internal">Internal</option>
                    <option value="Client">Client</option>
                    <option value="Support">Support</option>
                    <option value="Technical">Technical</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>

                  <Form.Control
                    type="date"
                    value={updateForm.dueDate}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        dueDate: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Ticket Reason</Form.Label>

                  <Select
                    options={ticketReasonOptions}
                    value={ticketReasonOptions.find(
                      (opt) => opt.value === updateForm.ticketReason,
                    )}
                    onChange={(opt) =>
                      setUpdateForm({
                        ...updateForm,
                        ticketReason: opt?.value || "",
                      })
                    }
                    isClearable
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Category</Form.Label>

                  <Form.Control
                    value={updateForm.category}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        category: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Assigned To</Form.Label>

                  <Select
                    isMulti
                    options={dynamicAssignees}
                    value={dynamicAssignees.filter((opt) =>
                      updateForm.assignedTo.includes(opt.value),
                    )}
                    onChange={(selected) =>
                      setUpdateForm({
                        ...updateForm,
                        assignedTo: selected
                          ? selected.map((o) => o.value)
                          : [],
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Submitted By</Form.Label>

                  <Select
                    options={dynamicAssignees}
                    value={dynamicAssignees.find(
                      (opt) => opt.value === updateForm.submittedBy,
                    )}
                    onChange={(selected) =>
                      setUpdateForm({
                        ...updateForm,
                        submittedBy: selected?.value || "",
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>

                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={updateForm.description}
                    onChange={(e) =>
                      setUpdateForm({
                        ...updateForm,
                        description: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Project Assigned</Form.Label>

                  <Select
                    isMulti
                    options={projectOptions}
                    value={projectOptions.filter((opt) =>
                      updateForm.projectAssigned
                        ?.split(",")
                        .includes(opt.value),
                    )}
                    onChange={async (selected) => {
                      const ids = selected ? selected.map((o) => o.value) : [];

                      // update form
                      setUpdateForm({
                        ...updateForm,
                        projectAssigned: ids.join(","),
                        assignedTo: [],
                        submittedBy: "",
                      });

                      // load assignees for each project
                      for (const pid of ids) {
                        if (!assigneeOptionsByProject?.[pid]) {
                          await fetchProjectAndPartners(pid);
                        }
                      }
                    }}
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="fields-btn-container">
              <Button
                variant="outline-info"
                onClick={() => setShowWorkLogModal(true)}
              >
                + Add Work Log
              </Button>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleUpdateTicket}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update Ticket"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Work Log Modal */}

      <Modal
        show={showWorkLogModal}
        onHide={() => setShowWorkLogModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Work Log</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Worked By <span className="text-red">**</span>
                  </Form.Label>

                  <Select
                    options={dynamicAssignees}
                    value={dynamicAssignees.find(
                      (o) => o.value === workLogForm.workedBy,
                    )}
                    onChange={(opt) =>
                      setWorkLogForm({
                        ...workLogForm,
                        workedBy: opt?.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Effort (Hours) <span className="text-red">**</span>
                  </Form.Label>

                  <Form.Control
                    type="number"
                    value={workLogForm.effortValue}
                    onChange={(e) =>
                      setWorkLogForm({
                        ...workLogForm,
                        effortValue: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Remarks</Form.Label>

                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={workLogForm.remarks}
                    onChange={(e) =>
                      setWorkLogForm({
                        ...workLogForm,
                        remarks: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowWorkLogModal(false)}
          >
            Cancel
          </Button>

          <Button variant="primary" onClick={handleAddWorkLog}>
            Save Work Log
          </Button>
        </Modal.Footer>
      </Modal>

      {/* <Modal show={showWorkLogModal} onHide={() => setShowWorkLogModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Work Log</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Worked By</Form.Label>

            <Select
              options={ticketAssignees}
              value={ticketAssignees.find(
                (o) => o.value === workLogForm.workedBy,
              )}
              onChange={(opt) =>
                setWorkLogForm({
                  ...workLogForm,
                  workedBy: opt?.value,
                })
              }
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Effort</Form.Label>
            <Form.Control
              type="number"
              value={workLogForm.effortValue}
              onChange={(e) =>
                setWorkLogForm({
                  ...workLogForm,
                  effortValue: e.target.value,
                })
              }
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={workLogForm.remarks}
              onChange={(e) =>
                setWorkLogForm({
                  ...workLogForm,
                  remarks: e.target.value,
                })
              }
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={() => setShowWorkLogModal(false)}>Cancel</Button>
          <Button onClick={handleAddWorkLog}>Save</Button>
        </Modal.Footer>
      </Modal> */}

      {/* Closure Modal */}

      <Modal
        show={showClosureModal}
        onHide={() => setShowClosureModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Close Ticket</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Closure Type <span className="text-red">**</span>
                  </Form.Label>

                  <Select
                    options={closureTypeOptions}
                    value={closureTypeOptions.find(
                      (o) => o.value === closureForm.closureTypeCode,
                    )}
                    onChange={(opt) =>
                      setClosureForm({
                        ...closureForm,
                        closureTypeCode: opt?.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Closure Remarks</Form.Label>

                  <Form.Control
                    as="textarea"
                    style={{ height: "27vh" }}
                    rows={12}
                    value={closureForm.closureRemarks}
                    onChange={(e) =>
                      setClosureForm({
                        ...closureForm,
                        closureRemarks: e.target.value,
                      })
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowClosureModal(false)}
          >
            Cancel
          </Button>

          <Button variant="danger" onClick={handleConfirmClosure}>
            Close Ticket
          </Button>
        </Modal.Footer>
      </Modal>

      {/* <Modal show={showClosureModal} onHide={() => setShowClosureModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Close Ticket</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Closure Type</Form.Label>

            <Select
              options={closureTypeOptions}
              value={closureTypeOptions.find(
                (o) => o.value === closureForm.closureTypeCode,
              )}
              onChange={(opt) =>
                setClosureForm({
                  ...closureForm,
                  closureTypeCode: opt?.value,
                })
              }
            />
          </Form.Group>

          <Form.Group className="mt-3">
            <Form.Label>Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={closureForm.closureRemarks}
              onChange={(e) =>
                setClosureForm({
                  ...closureForm,
                  closureRemarks: e.target.value,
                })
              }
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button onClick={() => setShowClosureModal(false)}>Cancel</Button>
          <Button variant="danger" onClick={handleConfirmClosure}>
            Close Ticket
          </Button>
        </Modal.Footer>
      </Modal> */}
    </>
  );
};

export default TicketEditModal;
